import React, { type ReactNode } from 'react'
import { getFrequencyDescription } from '../../../components/recipes/util'
import { formatScheduleOccurrence } from '../format'
import type { RecipeSchedule } from '../types'

export type ScheduledSegmentListRecipe = Omit<Models.Recipe, 'id'> & {
    id?: number | string
    schedule?: RecipeSchedule | null
}

export const formatProfileDateTime = (
    value: string | Date | null | undefined,
    timezone: string,
): string => {
    if (value == null) return 'Never'
    const date = typeof value === 'string' ? new Date(value) : value
    if (Number.isNaN(date.getTime())) return String(value)

    // Intl renders the localized clock as h:mm AM/PM and, unlike date-fns,
    // actually applies the active profile's time zone.
    let formatted: string
    let effectiveTimezone = timezone
    try {
        formatted = new Intl.DateTimeFormat(undefined, {
            timeZone: timezone,
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        }).format(date)
    } catch {
        effectiveTimezone = 'UTC'
        formatted = new Intl.DateTimeFormat(undefined, {
            timeZone: 'UTC',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        }).format(date)
    }

    return `${formatted} (${effectiveTimezone})`
}

export const ScheduledSegmentFrequency = ({
    recipe,
    fallback,
}: {
    recipe: ScheduledSegmentListRecipe
    fallback: ReactNode
}) => {
    if (!recipe.schedule) return <>{fallback}</>

    const nextRun = recipe.schedule.next_run_at
        ? formatScheduleOccurrence(recipe.schedule.next_run_at, recipe.schedule.timezone)
        : null

    return (
        <span title={nextRun ? `Next run: ${nextRun}` : undefined}>
            {getFrequencyDescription(recipe.frequency_params, recipe.schedule)}
        </span>
    )
}
